const KeenSlider = window.KeenSlider
const { debounce } = window.BAO.Utils
const { setInitialised, useDots, useArrows } = window.BAO.Carousel.plugins

class ProductImageCarousel extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['slides', 'slide']
  }

  constructor () {
    super()
    this.isDesktop = false // Initialise with default value
    this.isMobile = false // Initialise with default value
    this.handleResize = debounce(this.handleResize.bind(this), 200) // Debounce handleResize method
    this.hasPeek = false
  }

  connectedCallback () {
    super.connectedCallback()
    this.updateIsDesktop()
    this.updateIsMobile()
    this.setGalleryHeight()
    window.addEventListener('resize', this.handleResize)

    this.whenPeekReady().then(() => {
      this.hasPeek = this.hasGalleryPeek
      this.setupCarousel()
      if (this.isDesktop) {
        this.setupThumbnailCarousel()
      }
      this.observeGalleryPeek()
    })
  }

  disconnectedCallback () {
    super.disconnectedCallback()
    window.removeEventListener('resize', this.handleResize)
    this.peekObserver?.disconnect()
    this.destroyCarousel()
  }

  setupListeners () {
    super.setupListeners()

    if (!this.externalEls?.thumbnails?.exists) return

    this.handleThumbnailTrigger = this.handleThumbnailTrigger.bind(this)

    this.externalEls?.thumbnail.elements.forEach(thumbnailtriggerEl => {
      this.listeners.add(
        thumbnailtriggerEl,
        'click',
        this.handleThumbnailTrigger,
      )
    })
  }

  setGalleryHeight () {
    const galleryHeight = `${this.els?.slide?.element.offsetHeight}px`

    this.externalEls?.wrapper?.element.style.setProperty(
      '--GalleryHeight',
      galleryHeight,
    )
  }

  get hasGalleryPeek () {
    return (
      !this.isDesktop &&
      document.documentElement.getAttribute('data-gallery-peek') === 'true'
    )
  }

  get peekDecision () {
    return document.documentElement.getAttribute('data-gallery-peek')
  }

  whenPeekReady () {
    if (!this.hasAttribute('data-ab-gallery-peek')) {
      return Promise.resolve()
    }

    if (this.peekDecision === 'true' || this.peekDecision === 'false') {
      return Promise.resolve()
    }

    return new Promise(resolve => {
      const timeout = setTimeout(() => {
        observer.disconnect()
        resolve()
      }, 2000)

      const observer = new MutationObserver(() => {
        if (this.peekDecision !== 'true' && this.peekDecision !== 'false') {
          return
        }

        clearTimeout(timeout)
        observer.disconnect()
        resolve()
      })

      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-gallery-peek'],
      })
    })
  }

  getSlidesConfig () {
    if (!this.hasGalleryPeek) {
      return {
        perView: 1,
        spacing: 2,
      }
    }

    return (size, slides) => {
      const peek = 70
      const spacing = 0
      const slideSize = (size - peek) / size
      const configs = slides.map(() => ({
        size: slideSize,
        spacing,
        origin: 0,
      }))

      const totalSize =
        configs.reduce((sum, slide) => sum + slide.size, 0) +
        spacing * Math.max(configs.length - 1, 0)

      let distanceCovered = 0
      configs.forEach(slide => {
        const remaining = totalSize - distanceCovered
        distanceCovered += slide.size + spacing

        if (remaining < 1) {
          slide.origin = 1 - remaining - (totalSize > 1 ? 0 : 1 - totalSize)
        }
      })

      return configs
    }
  }

  observeGalleryPeek () {
    this.hasPeek = this.hasGalleryPeek

    this.peekObserver = new MutationObserver(() => {
      const hasPeek = this.hasGalleryPeek
      if (hasPeek === this.hasPeek) return

      this.hasPeek = hasPeek

      if (this.isDesktop) return

      this.destroyCarousel()
      this.setupCarousel()
      this.setGalleryHeight()
    })

    this.peekObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-gallery-peek'],
    })
  }

  /**
   * Sets up the carousel if the device is not desktop and there's more than one slide.
   */
  setupCarousel () {
    const slideCount = this.els.slides.element.children.length // Get the number of slides
    this.onSlideChanged = this.onSlideChanged.bind(this)

    if (slideCount <= 1) {
      this.setAttribute('data-initialised', 'false')
      return
    }

    const dots = Array.from(this.els.dots.element.children) // Get all the dot elements
    const arrows = {
      container: this.els?.arrows?.element,
      previous: this.els?.previous?.element,
      next: this.els?.next?.element,
    }

    this.carousel = new KeenSlider(
      this.els.slides.element,
      {
        selector: this.els.slide.selector,
        loop: !this.hasGalleryPeek,
        mode: 'snap',
        rubberband: false,
        slides: this.getSlidesConfig(),
        slideChanged: this.onSlideChanged,
        dragStarted: () => {
          this.els.slides.element.classList.add('pga-Gallery_Slides-dragging')
        },
        dragEnded: () => {
          this.els.slides.element.classList.remove(
            'pga-Gallery_Slides-dragging',
          )
        },
      },
      [setInitialised(), useDots(dots), useArrows(arrows)],
    )

    this.setAttribute('data-initialised', 'true')
  }

  setupThumbnailCarousel () {
    if (!this.externalEls?.thumbnails?.exists) return

    this.thumbnailCarousel = new KeenSlider(
      this.externalEls.thumbnails.element,
      {
        selector: this.externalEls.thumbnail.selector,
        loop: false,
        mode: 'snap',
        rubberband: false,
        vertical: true,
        slides: {
          perView: 'auto',
          spacing: 2,
        },
        dragStarted: () => {
          this.externalEls.thumbnails.element.classList.add(
            'pga-GalleryThumbnail_Slides-dragging',
          )
        },
        dragEnded: () => {
          this.externalEls.thumbnails.element.classList.remove(
            'pga-GalleryThumbnail_Slides-dragging',
          )
        },
      },
      [setInitialised()],
    )

    this.externalEls.thumbnails.element.setAttribute('data-initialised', 'true')
  }

  handleThumbnailTrigger (e) {
    const thumbnailTrigger = e.target.closest(
      `${this.externalEls?.thumbnail.selector}`,
    )

    const targetIndex = parseInt(thumbnailTrigger.dataset.index, 10)

    this.carousel.moveToIdx(targetIndex)
  }

  onSlideChanged (instance) {
    if (!this.externalEls?.thumbnails?.exists) return

    const currentSlide = instance.track.details.rel

    this.externalEls.thumbnail.elements.forEach((thumbnail, index) => {
      thumbnail.setAttribute('aria-current', index === currentSlide)
    })
  }

  /**
   * Destroys the carousel if it exists.
   */
  destroyCarousel () {
    if (this.carousel) {
      this.carousel.destroy()
      this.carousel = null

      this.setAttribute('data-initialised', 'false')
    }
  }

  destroyThumbnailCarousel () {
    if (this.thumbnailCarousel) {
      this.thumbnailCarousel.destroy()
      this.thumbnailCarousel = null

      this.externalEls.thumbnails.element.setAttribute(
        'data-initialised',
        'false',
      )
    }
  }

  /**
   * Checks if the device is in desktop mode and updates the isDesktop flag accordingly.
   * @returns {boolean} True if the value of isDesktop has changed, false otherwise.
   */
  updateIsDesktop () {
    const isDesktop = window.innerWidth >= 901

    if (isDesktop !== this.isDesktop) {
      this.isDesktop = isDesktop
      return true // Indicates the value changed
    }
    return false // Value remains the same
  }

  updateIsMobile () {
    const isMobile = window.innerWidth <= 900

    if (isMobile !== this.isMobile) {
      this.isMobile = isMobile
      return true // Indicates the value changed
    }
    return false // Value remains the same
  }

  /**
   * Handles the window resize event, updating the carousel setup if necessary.
   * Peek only applies on mobile; desktop keeps the normal full-width carousel + thumbnails.
   */
  handleResize () {
    const desktopChanged = this.updateIsDesktop()
    this.updateIsMobile()

    if (!desktopChanged) {
      this.setGalleryHeight()
      return
    }

    this.destroyCarousel()
    this.hasPeek = this.hasGalleryPeek
    this.setupCarousel()

    if (this.isDesktop) {
      this.setupThumbnailCarousel()
    } else {
      this.destroyThumbnailCarousel()
    }

    this.setGalleryHeight()
  }
}

if (!customElements.get('product-image-carousel')) {
  customElements.define('product-image-carousel', ProductImageCarousel)
}
